import { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import useApiMethods from "../../../lib/useApiMethods";
import { EMPLOYEE_CANDIDATE_OFFER_LETTER, EMPLOYEE_CANDIDATE_VERIFICATION, EMPLOYEE_INTERN_VERIFICATION } from "../../../constants/api";
import ToastMsg from "../../../components/common/ToastMsg";
import { DatePicker, Select, Upload } from "antd";
import { Textarea } from "../../../components/ui/input";
import { cn } from "../../../utils/tailwindUtils";
import dayjs from "dayjs";
import { UploadOutlined, DeleteOutlined, EyeOutlined, FileOutlined } from "@ant-design/icons";

const { Dragger } = Upload;

const extractImageName = (url) => {
    let result = "";
    if (url) {
        result = url.substring(url.lastIndexOf('/') + 1, url.lastIndexOf('?'))
    }
    return result
}

const FinalVerificationContent = forwardRef(({ employeeData = {}, dropdown, employeeID = null }, ref) => {
    const { fetch, update } = useApiMethods();
    const location = useLocation();
    const navigate = useNavigate();

    const url = location.pathname === '/employee/onboarding/viewDetails' ?
        EMPLOYEE_CANDIDATE_VERIFICATION : location.pathname === '/intern/onboarding/viewDetails' ?
            EMPLOYEE_INTERN_VERIFICATION : '';

    // States
    const [loading, setLoading] = useState(true);
    const [formData, setFormData] = useState({
        // Onboarding Process Details
        document_verification_date: null,
        document_verification_status: null,
        document_verified_by: null,
        document_verification_comments: '',
        background_verification_date: null,
        background_verification_status: null,
        background_verified_by: null,
        background_verification_comments: '',

        // Onboarding Document Details
        joining_date: null,
        letter_type: null,
        letter_template: null,
        processed_by: null,
        approved_by: null,
        letter_status: null,
        comments: '',
        document: null,
        send_mail: false
    });

    const [newDocument, setNewDocument] = useState(null); // For new file upload
    const [documentFile, setDocumentFile] = useState(null); // For file object
    const [dropdownData, setDropdownData] = useState(dropdown || {}); // --> setstate is not used here but decleared for the future use if needed

    // Fetch verification data
    const fetchVerification = async () => {
        try {
            setLoading(true);
            if (!employeeID || !url) {
                ToastMsg('error', 'Employee ID or URL is missing');
                setLoading(false);
                return;
            }

            const response = await fetch(url, { employee_id: employeeID });

            if (response.status !== 'fail' && response.data) {
                const { onboarding_process_details, onboarding_document_details } = response.data;

                // Update form data with API response
                if (onboarding_process_details) {
                    setFormData(prev => ({
                        ...prev,
                        document_verification_date: onboarding_process_details.document_verification_date
                            ? dayjs(onboarding_process_details.document_verification_date)
                            : null,
                        document_verification_status: onboarding_process_details.document_verification_status || null,
                        document_verified_by: onboarding_process_details.document_verified_by || null,
                        document_verification_comments: onboarding_process_details.document_verification_comments || '',
                        background_verification_date: onboarding_process_details.background_verification_date
                            ? dayjs(onboarding_process_details.background_verification_date)
                            : null,
                        background_verification_status: onboarding_process_details.background_verification_status || null,
                        background_verified_by: onboarding_process_details.background_verified_by || null,
                        background_verification_comments: onboarding_process_details.background_verification_comments || ''
                    }));
                }

                // If there are document details, use the first one (assuming single document)
                if (onboarding_document_details && onboarding_document_details.length > 0) {
                    const doc = onboarding_document_details[0];
                    setFormData(prev => ({
                        ...prev,
                        joining_date: doc.joining_date ? dayjs(doc.joining_date) : null,
                        letter_type: doc.letter_type || null,
                        letter_template: doc.letter_template || null,
                        processed_by: doc.processed_by || null,
                        approved_by: doc.approved_by || null,
                        letter_status: doc.letter_status || null,
                        comments: doc.comments || '',
                        document: doc.document || null,
                        send_mail: doc.send_mail || false
                    }));
                }
            }
        } catch (error) {
            ToastMsg('error', error.message || 'Failed to fetch verification data');
        } finally {
            setLoading(false);
        }
    };

    // Handle form field changes
    const handleChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    // Handle file upload
    const handleFileUpload = (file) => {
        // Validate file type
        const allowedTypes = [
            'application/pdf',
            'application/msword',
            'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
            'image/jpeg',
            'image/png',
            'image/jpg'
        ];

        if (!allowedTypes.includes(file.type)) {
            ToastMsg('error', 'Only PDF, DOC, DOCX, JPG, PNG files are allowed');
            return false;
        }

        // Validate file size (5MB max)
        const maxSize = 5 * 1024 * 1024; // 5MB
        if (file.size > maxSize) {
            ToastMsg('error', 'File size must be less than 5MB');
            return false;
        }

        setDocumentFile(file);
        setNewDocument(URL.createObjectURL(file));

        // Clear the existing document URL since we're uploading a new one
        handleChange('document', null);

        return false; // Prevent automatic upload
    };

    // Remove uploaded file
    const handleRemoveFile = () => {
        setDocumentFile(null);
        setNewDocument(null);

        // Revoke object URL to prevent memory leak
        if (newDocument) {
            URL.revokeObjectURL(newDocument);
        }
    };

    // Remove existing document
    const handleRemoveExistingDocument = () => {
        handleChange('document', null);
    };

    const handleVerify = (move_to_employee = false, send_mail = false) => {
        // Helper function to format dates
        const formatDateIfExists = (date) => {
            if (date) {
                return dayjs(date).format("YYYY-MM-DD");
            }
            return date;
        };

        // Prepare onboarding_process_details
        const onboarding_process_details = {};

        // Add all process detail fields, even if null/empty
        onboarding_process_details.document_verification_date = formatDateIfExists(formData.document_verification_date);
        onboarding_process_details.document_verification_status = formData.document_verification_status;
        onboarding_process_details.document_verified_by = formData.document_verified_by;
        onboarding_process_details.document_verification_comments = formData.document_verification_comments || "";
        onboarding_process_details.background_verification_date = formatDateIfExists(formData.background_verification_date);
        onboarding_process_details.background_verification_status = formData.background_verification_status;
        onboarding_process_details.background_verified_by = formData.background_verified_by;
        onboarding_process_details.background_verification_comments = formData.background_verification_comments || "";

        // Prepare onboarding_document_details
        const onboarding_document_details = [{
            joining_date: formatDateIfExists(formData.joining_date),
            letter_type: formData.letter_type,
            letter_template: formData.letter_template,
            processed_by: formData.processed_by,
            approved_by: formData.approved_by,
            letter_status: formData.letter_status,
            comments: formData.comments || "",
            document: "", // Always include document field, even if empty
            send_mail: send_mail ? "True" : "False",
            employee: employeeID
        }];

        // Handle document extraction if it exists
        if (formData.document && typeof formData.document === 'string') {
            onboarding_document_details[0].document = extractImageName(formData.document);
        }

        // Filter out null values for process details
        const filteredProcessDetails = {};
        Object.keys(onboarding_process_details).forEach(key => {
            if (onboarding_process_details[key] !== null && onboarding_process_details[key] !== undefined) {
                filteredProcessDetails[key] = onboarding_process_details[key];
            }
        });

        // Filter out null values for document details
        const filteredDocumentDetails = onboarding_document_details.map(item => {
            const filteredItem = {};
            Object.keys(item).forEach(key => {
                if (item[key] !== null && item[key] !== undefined) {
                    filteredItem[key] = item[key];
                }
            });
            return filteredItem;
        });

        // Create the final payload
        const modifiedInfo = {
            employee_id: employeeID,
            move_to_employee: move_to_employee,
            send_mail: send_mail
        };

        // Always include these fields, even if empty
        if (Object.keys(filteredProcessDetails).length > 0) {
            modifiedInfo.onboarding_process_details = JSON.stringify(filteredProcessDetails);
        } else {
            modifiedInfo.onboarding_process_details = "{}"; // Send empty object if no data
        }

        // Always include document details
        modifiedInfo.onboarding_document_details = JSON.stringify(filteredDocumentDetails);

        // Add file separately if exists
        if (documentFile) {
            modifiedInfo[`document__0`] = documentFile;
        }

        console.log("Verification data to send:", modifiedInfo);
        return modifiedInfo;
    };

    const generateOfferLetter = async () => {
        try {
            const response = await fetch(EMPLOYEE_CANDIDATE_OFFER_LETTER, { employee_id: employeeID, template_id: formData.letter_template }, setLoading)
            const fileBlob = new Blob([response], { type: "application/pdf" });
            const fileURL = URL.createObjectURL(fileBlob);
            window.open(fileURL);
        } catch (error) {
            ToastMsg('error', error.message)
        }
    }

    // every actions for the parent declared here
    useImperativeHandle(ref, () => ({
        handleGenerateOfferLetter: async () => {
            try {
                setLoading(true);
                await generateOfferLetter()
                ToastMsg('success', 'Offer Letter Generated Successfully');
                return true;
            } catch (error) {
                ToastMsg('error', 'Failed to generate offer letter');
                return false;
            } finally {
                setLoading(false);
            }
        },

        handleSendMail: async () => {
            try {
                setLoading(true);
                const verificationData = handleVerify(false, true);

                // Call your API to save and send mail
                const response = await update(url, verificationData, true);

                if (response.status === 'success') {
                    ToastMsg('success', 'Successfully Mail has been sent');
                    return true;
                } else {
                    ToastMsg('error', response.message || 'Failed to send mail');
                    return false;
                }
            } catch (error) {
                ToastMsg('error', 'Failed to send a mail');
                return false;
            } finally {
                setLoading(false);
            }
        },

        handleMoveToEmployee: async () => {
            try {
                setLoading(true);
                const verificationData = handleVerify(true, false);

                // Call your API to save and move to employee
                const response = await update(url, verificationData, true);

                if (response.status === 'success') {
                    navigate(-1)
                    ToastMsg('success', 'Successfully Moved to employee list');
                    return true;
                } else {
                    ToastMsg('error', response.message || 'Failed to move to employee');
                    return false;
                }
            } catch (error) {
                ToastMsg('error', 'Failed to move to employee');
                return false;
            } finally {
                setLoading(false);
            }
        },

        handleVerifyDetails: async () => {
            try {
                setLoading(true);
                const verificationData = handleVerify();

                // // Call your API to save verification data
                const response = await update(url, verificationData, true);

                if (response.status === 'success') {
                    ToastMsg('success', 'Verification data saved successfully');
                    return true;
                } else {
                    ToastMsg('error', response.message || 'Failed to save verification data');
                    return false;
                }
            } catch (error) {
                ToastMsg('error', 'Failed to save verification data');
                return false;
            } finally {
                setLoading(false);
            }
        },
    }));

    // Prepare dropdown options
    const getDropdownOptions = (key) => {
        if (!dropdownData[key]) return [];

        // Use a Set to track unique values and prevent duplicates
        const seen = new Set();
        const uniqueOptions = [];

        dropdownData[key].forEach(item => {
            const value = item.id;
            if (!seen.has(value)) {
                seen.add(value);
                uniqueOptions.push({
                    label: item.name || item.letter_type_name || item.display_name || item.code || item.id,
                    value: value
                });
            }
        });

        return uniqueOptions;
    };

    const filterOption = (input, option) => {
        try {
            const label = option?.label;
            const searchText = (input || '').toLowerCase();

            // Convert label to string safely
            const labelString = String(label || '').toLowerCase();
            return labelString.includes(searchText);
        } catch (error) {
            console.warn('Filter error:', error);
            return true; // Return true to show all options if filter fails
        }
    };

    // Get employee options for verified by, processed by, approved by fields
    const getEmployeeOptions = () => {
        return getDropdownOptions('empinfo');
    };

    // Get letter type options
    const getLetterTypeOptions = () => {
        return getDropdownOptions('lettertype');
    };

    // Get letter template options
    const getLetterTemplateOptions = () => {
        return getDropdownOptions('lettertemp');
    };

    // Status options
    const statusOptions = [
        { label: 'Verified', value: 'verified' },
        { label: 'Not Verified', value: 'not verified' }
    ];

    const letterStatusOptions = [
        { label: 'Provided', value: 'provided' },
        { label: 'Not Provided', value: 'not provided' }
    ];

    // Get file icon based on type
    const getFileIcon = (url) => {
        if (url.includes('.pdf')) return 'PDF';
        if (url.includes('.doc') || url.includes('.docx')) return 'DOC';
        if (url.includes('.jpg') || url.includes('.jpeg') || url.includes('.png')) return 'IMG';
        return 'FILE';
    };

    // Initial fetch
    useEffect(() => {
        if (employeeID && url) {
            fetchVerification();
        }
    }, [employeeID, url]);

    // Clean up object URLs on unmount
    useEffect(() => {
        return () => {
            if (newDocument) {
                URL.revokeObjectURL(newDocument);
            }
        };
    }, [newDocument]);

    // Loader
    if (loading) {
        return (
            <div className="h-[calc(100vh-100px)] dark:bg-gray-900 flex items-center justify-center rounded-md">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sidebar-primary"></div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex justify-between items-start">
                <div>
                    <h2 className="text-md font-bold text-gray-900 dark:text-white">
                        Onboarding Process Details
                    </h2>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                        Complete employee verification process track
                    </p>
                </div>
                <button
                    onClick={() => {
                        setFormData({
                            document_verification_date: null,
                            document_verification_status: null,
                            document_verified_by: null,
                            document_verification_comments: '',
                            background_verification_date: null,
                            background_verification_status: null,
                            background_verified_by: null,
                            background_verification_comments: '',
                            joining_date: null,
                            letter_type: null,
                            letter_template: null,
                            processed_by: null,
                            approved_by: null,
                            letter_status: null,
                            comments: '',
                            document: null,
                            send_mail: false
                        });
                        handleRemoveFile();
                    }}
                    className="px-4 py-2 text-sm text-red-600 hover:text-red-800 border border-red-300 hover:border-red-500 rounded-md transition-colors"
                    type="button"
                >
                    Clear All
                </button>
            </div>

            {/* Document Verification Section */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block">
                        Document Verification date
                    </label>
                    <DatePicker
                        className="w-full"
                        value={formData.document_verification_date}
                        onChange={(date) => handleChange('document_verification_date', date)}
                        format="YYYY-MM-DD"
                    />
                </div>

                <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block">
                        Document Verification Status
                    </label>
                    <Select
                        className="w-full"
                        size="large"
                        value={formData.document_verification_status}
                        onChange={(value) => handleChange('document_verification_status', value)}
                        options={statusOptions}
                        placeholder="Select status"
                        allowClear
                    />
                </div>

                <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block">
                        Verified By
                    </label>
                    <Select
                        className="w-full"
                        size="large"
                        value={formData.document_verified_by}
                        onChange={(value) => handleChange('document_verified_by', value)}
                        options={getEmployeeOptions()}
                        placeholder="Select employee"
                        showSearch
                        filterOption={filterOption}
                        allowClear
                    />
                </div>

                <div className="space-y-2 col-span-1 md:col-span-3">
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block">
                        Comments
                    </label>
                    <Textarea
                        value={formData.document_verification_comments}
                        onChange={(e) => handleChange('document_verification_comments', e.target.value)}
                        className={cn(
                            "w-full rounded-md transition-colors duration-200",
                            "border-gray-300 dark:border-gray-600",
                            "bg-white dark:bg-gray-700 text-gray-900 dark:text-white",
                            "focus:border-sidebar-primary focus:ring-2 focus:ring-sidebar-primary/20",
                            "disabled:bg-gray-100 disabled:text-gray-500 dark:disabled:bg-gray-800 dark:disabled:text-gray-400"
                        )}
                        placeholder="Enter comments for document verification"
                        rows={3}
                    />
                </div>
            </div>

            {/* Background Verification Section */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block">
                        Background Verification date
                    </label>
                    <DatePicker
                        className="w-full"
                        value={formData.background_verification_date}
                        onChange={(date) => handleChange('background_verification_date', date)}
                        format="YYYY-MM-DD"
                    />
                </div>

                <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block">
                        Background Verification Status
                    </label>
                    <Select
                        className="w-full"
                        size="large"
                        value={formData.background_verification_status}
                        onChange={(value) => handleChange('background_verification_status', value)}
                        options={statusOptions}
                        placeholder="Select status"
                        allowClear
                    />
                </div>

                <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block">
                        Verified By
                    </label>
                    <Select
                        className="w-full"
                        size="large"
                        value={formData.background_verified_by}
                        onChange={(value) => handleChange('background_verified_by', value)}
                        options={getEmployeeOptions()}
                        placeholder="Select employee"
                        showSearch
                        filterOption={filterOption}
                        allowClear
                    />
                </div>

                <div className="space-y-2 col-span-1 md:col-span-3">
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block">
                        Comments
                    </label>
                    <Textarea
                        value={formData.background_verification_comments}
                        onChange={(e) => handleChange('background_verification_comments', e.target.value)}
                        className={cn(
                            "w-full rounded-md transition-colors duration-200",
                            "border-gray-300 dark:border-gray-600",
                            "bg-white dark:bg-gray-700 text-gray-900 dark:text-white",
                            "focus:border-sidebar-primary focus:ring-2 focus:ring-sidebar-primary/20",
                            "disabled:bg-gray-100 disabled:text-gray-500 dark:disabled:bg-gray-800 dark:disabled:text-gray-400"
                        )}
                        placeholder="Enter comments for background verification"
                        rows={3}
                    />
                </div>
            </div>

            {/* Onboarding Document Details Section */}
            <div className="border-t-2 pt-6 space-y-4">
                <div className="flex justify-between items-start">
                    <div>
                        <h2 className="text-md font-bold text-gray-900 dark:text-white">
                            Onboarding Document Details
                        </h2>
                        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                            Complete employee professional documents for verification
                        </p>
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block">
                            Joining Date
                        </label>
                        <DatePicker
                            className="w-full"
                            value={formData.joining_date}
                            onChange={(date) => handleChange('joining_date', date)}
                            format="YYYY-MM-DD"
                        />
                    </div>

                    <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block">
                            Letter Type
                        </label>
                        <Select
                            className="w-full"
                            size="large"
                            value={formData.letter_type}
                            onChange={(value) => handleChange('letter_type', value)}
                            options={getLetterTypeOptions()}
                            placeholder="Select letter type"
                            allowClear
                        />
                    </div>

                    <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block">
                            Letter Template
                        </label>
                        <Select
                            className="w-full"
                            size="large"
                            value={formData.letter_template}
                            onChange={(value) => handleChange('letter_template', value)}
                            options={getLetterTemplateOptions()}
                            placeholder="Select letter template"
                            allowClear
                        />
                    </div>

                    <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block">
                            Processed By
                        </label>
                        <Select
                            className="w-full"
                            size="large"
                            value={formData.processed_by}
                            onChange={(value) => handleChange('processed_by', value)}
                            options={getEmployeeOptions()}
                            placeholder="Select employee"
                            showSearch
                            filterOption={filterOption}
                            allowClear
                        />
                    </div>

                    <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block">
                            Approved By
                        </label>
                        <Select
                            className="w-full"
                            size="large"
                            value={formData.approved_by}
                            onChange={(value) => handleChange('approved_by', value)}
                            options={getEmployeeOptions()}
                            placeholder="Select employee"
                            showSearch
                            filterOption={filterOption}
                            allowClear
                        />
                    </div>

                    <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block">
                            Letter Status
                        </label>
                        <Select
                            className="w-full"
                            size="large"
                            value={formData.letter_status}
                            onChange={(value) => handleChange('letter_status', value)}
                            options={letterStatusOptions}
                            placeholder="Select status"
                            allowClear
                        />
                    </div>
                </div>

                <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block">
                        Comments
                    </label>
                    <Textarea
                        value={formData.comments}
                        onChange={(e) => handleChange('comments', e.target.value)}
                        className={cn(
                            "w-full rounded-md transition-colors duration-200",
                            "border-gray-300 dark:border-gray-600",
                            "bg-white dark:bg-gray-700 text-gray-900 dark:text-white",
                            "focus:border-sidebar-primary focus:ring-2 focus:ring-sidebar-primary/20",
                            "disabled:bg-gray-100 disabled:text-gray-500 dark:disabled:bg-gray-800 dark:disabled:text-gray-400"
                        )}
                        placeholder="Enter comments for onboarding documents"
                        rows={3}
                    />
                </div>

                {/* Document Section */}
                <div className="space-y-4">
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block">
                        Upload Document
                    </label>

                    {/* Existing Document */}
                    {formData.document && (
                        <div className="p-4 border dark:border-gray-600 rounded-md bg-gray-50 dark:bg-gray-800">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                    <FileOutlined className="text-xl text-gray-500" />
                                    <div>
                                        <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                            {getFileIcon(formData.document)} Document
                                        </p>
                                        <a
                                            href={formData.document}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-xs text-sidebar-primary hover:underline flex items-center gap-1"
                                        >
                                            <EyeOutlined /> View Document
                                        </a>
                                    </div>
                                </div>
                                <button
                                    onClick={handleRemoveExistingDocument}
                                    className="text-red-500 hover:text-red-700"
                                    title="Remove document"
                                >
                                    <DeleteOutlined />
                                </button>
                            </div>
                        </div>
                    )}

                    {/* New Document Upload */}
                    {!formData.document && !newDocument && (
                        <Dragger
                            accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                            beforeUpload={handleFileUpload}
                            showUploadList={false}
                            maxCount={1}
                            className="p-1 dark:border-gray-600 rounded-md hover:border-sidebar-primary"
                        >
                            <div className="p-8 text-center">
                                <UploadOutlined className="text-3xl text-gray-400 mb-4" />
                                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                                    Click or drag file to upload
                                </p>
                                <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                                    Supports PDF, DOC, DOCX, JPG, PNG (Max: 5MB)
                                </p>
                            </div>
                        </Dragger>
                    )}

                    {/* New Document Preview */}
                    {newDocument && (
                        <div className="p-4 border border-gray-300 dark:border-gray-600 rounded-md bg-gray-50 dark:bg-gray-800">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                    <FileOutlined className="text-xl text-gray-500" />
                                    <div>
                                        <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                            {documentFile?.name}
                                        </p>
                                        <p className="text-xs text-gray-500">
                                            {(documentFile?.size / (1024 * 1024)).toFixed(2)} MB
                                        </p>
                                    </div>
                                </div>
                                <div className="flex items-center gap-2">
                                    <button
                                        onClick={() => window.open(newDocument, '_blank')}
                                        className="text-sidebar-primary hover:text-sidebar-primary/80"
                                        title="Preview"
                                    >
                                        <EyeOutlined />
                                    </button>
                                    <button
                                        onClick={handleRemoveFile}
                                        className="text-red-500 hover:text-red-700"
                                        title="Remove"
                                    >
                                        <DeleteOutlined />
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
});

export default FinalVerificationContent;